import { format, isAfter, isBefore, isSameDay } from 'date-fns';
import { nl } from 'date-fns/locale';

import { Eenheid, StatusOvergangen } from '@/types/werklijnenInline';
import { formatMomentMetWeekgrens, getDutchDayAbbreviation } from '@/utils/dateUtils';
import { sorteerStatusOvergangenZonderPrognoseInTijdVenster } from '@/utils/statusOvergangUtil';

export type NietInzetbaarIcoon = 'zandloper' | 'nietInzetbaar';

export type NietInzetbaarPeriode = {
  label: string;
  icoon: NietInzetbaarIcoon;
  /** Doorgestreept deel ná `label`, gezet wanneer de getoonde verwacht-gereed-prognose al verstreken is. */
  overschredenTijdstip?: string;
};

/**
 * Bepaalt of, en zo ja hoe, de "niet inzetbaar"-pill getoond moet worden voor een eenheid.
 *
 * Er zijn 4 scenario's, gebaseerd op 2 booleans:
 * - is de eenheid nu al NIET_INZETBAAR, of wordt dat pas later (binnen de mwd-horizon)?
 * - is er een geldige VERWACHT_GEREED-prognose bekend, binnen de mwd-horizon?
 *
 * | # | nu al niet-inzetbaar | geldige prognose | label            | icoon         |
 * |---|----------------------|-------------------|------------------|---------------|
 * | 1 | nee                  | nee               | `Vanaf <tijd>`   | zandloper     |
 * | 2 | ja                   | ja                | `Tot ~<tijd>`    | nietInzetbaar |
 * | 3 | nee                  | ja                | `<tijd>~<tijd>`  | zandloper     |
 * | 4 | ja                   | nee               | `Nu`             | nietInzetbaar |
 *
 * Retourneert undefined wanneer de eenheid niet (nu of straks, binnen de mwd-horizon) niet-inzetbaar is.
 */
export const bepaalNietInzetbaarPeriode = (
  eenheid: Eenheid | undefined,
  horizonEinde: Date,
  nu: Date = new Date(),
): NietInzetbaarPeriode | undefined => {
  if (!eenheid) return undefined;

  const nietInzetbaarPeriode = bepaalHuidigeOfEerstvolgendeNietInzetbaarPeriode(eenheid, horizonEinde, nu);
  if (!nietInzetbaarPeriode) return undefined;
  const { begin: nietInzetbaarBegin, isActief } = nietInzetbaarPeriode;

  const verwachtGereed = bepaalGeldigeVerwachtGereedPrognose(eenheid, nietInzetbaarBegin, horizonEinde);

  if (isActief && verwachtGereed) {
    if (isBefore(verwachtGereed, nu)) {
      return { ...formatTotOverschreden(verwachtGereed, nu), icoon: 'nietInzetbaar' };
    }
    return { label: formatTot(verwachtGereed, nu), icoon: 'nietInzetbaar' };
  }
  if (isActief) {
    return { label: 'Nu', icoon: 'nietInzetbaar' };
  }
  if (verwachtGereed) {
    return { label: formatBereik(nietInzetbaarBegin, verwachtGereed, nu), icoon: 'zandloper' };
  }
  return { label: `Vanaf ${formatTijdstip(nietInzetbaarBegin, nu)}`, icoon: 'zandloper' };
};

// --- Statusovergangen: bepalen van de relevante niet-inzetbaar- en verwacht-gereed-momenten ---

type NietInzetbaarPeriodeStart = {
  /** Het moment waarop de niet-inzetbaar-periode begint (of al begonnen is). */
  begin: Date;
  /** True wanneer de eenheid op `nu` al niet-inzetbaar is; false wanneer dit pas in de toekomst gebeurt. */
  isActief: boolean;
};

/**
 * Bepaalt het begin van de relevante niet-inzetbaar-periode:
 * - als de eenheid nu al niet-inzetbaar is, is dat het begin van de huidige periode;
 * - anders wordt gezocht naar het eerstvolgende toekomstige NIET_INZETBAAR-moment binnen de mwd-horizon.
 *
 * Retourneert undefined wanneer de eenheid niet (nu of straks, binnen de horizon) niet-inzetbaar is.
 */
const bepaalHuidigeOfEerstvolgendeNietInzetbaarPeriode = (
  eenheid: Eenheid,
  horizonEinde: Date,
  nu: Date,
): NietInzetbaarPeriodeStart | undefined => {
  const huidigeOvergang = sorteerStatusOvergangenZonderPrognoseInTijdVenster(eenheid, nu)?.find(
    (overgang) => !isAfter(new Date(overgang.begindatumtijd), nu),
  );

  if (huidigeOvergang?.status === 'NIET_INZETBAAR') {
    return { begin: new Date(huidigeOvergang.begindatumtijd), isActief: true };
  }

  const eerstvolgende = vindEerstvolgendeOvergangNa(eenheid, 'NIET_INZETBAAR', nu, horizonEinde);
  return eerstvolgende ? { begin: new Date(eerstvolgende.begindatumtijd), isActief: false } : undefined;
};

/**
 * Zoekt de eerstvolgende VERWACHT_GEREED-prognose ná het begin van de niet-inzetbaar-periode, en
 * geeft deze alleen terug wanneer hij binnen de mwd-horizon valt (anders telt hij niet als
 * "geldige prognose").
 */
const bepaalGeldigeVerwachtGereedPrognose = (
  eenheid: Eenheid,
  nietInzetbaarBegin: Date,
  horizonEinde: Date,
): Date | undefined => {
  const verwachtGereed = vindEerstvolgendeOvergangNa(eenheid, 'VERWACHT_GEREED', nietInzetbaarBegin, undefined);
  if (!verwachtGereed) return undefined;

  const verwachtGereedDatum = new Date(verwachtGereed.begindatumtijd);
  return verwachtGereedDatum <= horizonEinde ? verwachtGereedDatum : undefined;
};

/**
 * Zoekt, binnen `eenheid.statusovergangen`, het chronologisch eerstvolgende moment dat voldoet aan:
 * - de opgegeven `status`;
 * - begint ná `ondergrens` (exclusief) — bv. "nu", of het begin van de niet-inzetbaar-periode;
 * - en, als `horizonEinde` is opgegeven, niet later begint dan `horizonEinde` (inclusief) — de
 *   mwd-horizon waarbuiten toekomstige data niet wordt meegenomen.
 */
const vindEerstvolgendeOvergangNa = (
  eenheid: Eenheid,
  status: StatusOvergangen['status'],
  ondergrens: Date,
  horizonEinde: Date | undefined,
): StatusOvergangen | undefined =>
  eenheid.statusovergangen
    ?.filter((overgang) => {
      if (overgang.status !== status) return false;
      const begindatumtijd = new Date(overgang.begindatumtijd);
      if (!isAfter(begindatumtijd, ondergrens)) return false;
      return horizonEinde === undefined || begindatumtijd <= horizonEinde;
    })
    .sort((a, b) => new Date(a.begindatumtijd).getTime() - new Date(b.begindatumtijd).getTime())[0];

// --- Formatteren van de pill-tekst ---

const formatTijd = (datum: Date): string => format(datum, 'HH:mm', { locale: nl });

/** Tijdstip in compacte notatie rond nu, anders met datum (bv. '8:00', 'do 8:00' of '10-8 19:00'). */
const formatTijdstip = (datum: Date, nu: Date): string =>
  isSameDay(datum, nu) ? formatTijd(datum) : formatMomentMetWeekgrens(datum, nu);

/** Bereik van begin t/m eind, bv. '8:00~17:00', 'do 8:00~17:00' of 'do 20:00 ~ vr 8:00'. */
const formatBereik = (begin: Date, eind: Date, nu: Date): string => {
  if (isSameDay(begin, eind)) {
    if (isSameDay(begin, nu)) {
      return `${formatTijd(begin)}~${formatTijd(eind)}`;
    }
    return `${formatMomentMetWeekgrens(begin, nu)}~${formatTijd(eind)}`;
  }
  return `${formatTijdstip(begin, nu)} ~ ${formatTijdstip(eind, nu)}`;
};

/** "Tot"-label, bv. 'Tot ~17:00' of 'Tot ~ wo 17:00'. */
const formatTot = (eind: Date, nu: Date): string =>
  isSameDay(eind, nu) ? `Tot ~${formatTijd(eind)}` : `Tot ~ ${formatMomentMetWeekgrens(eind, nu)}`;

/** "Tot"-label voor een verstreken verwacht-gereed-prognose, met het tijdstip-deel apart voor de doorgestreepte weergave. */
const formatTotOverschreden = (eind: Date, nu: Date): { label: string; overschredenTijdstip: string } => {
  const tijdstip = formatMomentMetWeekgrens(eind, nu);
  const weekNotatie = `${getDutchDayAbbreviation(eind)} ${formatTijd(eind)}`;
  const separator = tijdstip === weekNotatie ? ' ~ ' : ' ~';
  return { label: 'Tot', overschredenTijdstip: `${separator}${tijdstip}` };
};
